import { useMemo } from 'react'
import { useQueryParams } from 'use-query-params'
import {
  stringQueryParamEncoder,
  usePaginationQueryParams,
  makeEnumQueryParamEncoder,
} from '@retailer-platform/dashboard/utils'
import {
  type ListApprovalRequestsQueryVariables,
  useListApprovalRequestsQuery,
  type ContentSearchInput,
  type ApprovalRequestState,
} from '../../api'
import { AllApprovalRequestStates } from '../../api/common'

export const approvalRequestsQueryParamsEncoders = {
  name: stringQueryParamEncoder,
  status: makeEnumQueryParamEncoder(AllApprovalRequestStates, stringQueryParamEncoder),
  tool: stringQueryParamEncoder,
}

type SearchProps = {
  variables?: Partial<ListApprovalRequestsQueryVariables>
}

export const useApprovalRequestsSearch = (props: SearchProps = {}) => {
  const { variables } = props
  const [queryParams] = useQueryParams(approvalRequestsQueryParamsEncoders)
  const [pageQueryParams] = usePaginationQueryParams()
  const { page, itemsPerPage } = pageQueryParams
  const { name, status, tool } = queryParams || {}

  const newVars = useMemo(() => {
    const newVars: ListApprovalRequestsQueryVariables = {
      ...variables,
    }

    if (name) newVars.ilikeName = name
    if (tool) {
      newVars.contentSearches = [{ equalsContentType: tool } as ContentSearchInput]
    }
    if (status) {
      newVars.includesStates = [status as ApprovalRequestState]
    }
    const perPage = itemsPerPage || 20
    if (page) newVars.offset = (page - 1) * perPage
    if (perPage) newVars.limit = perPage

    return newVars
  }, [name, status, tool, variables, page, itemsPerPage])

  const { data, previousData, ...rest } = useListApprovalRequestsQuery({
    variables: newVars,
  })

  return {
    approvalRequests: (data || previousData)?.approvalsManagementListRequests.approvalRequests,
    totalCount: (data || previousData)?.approvalsManagementListRequests.totalCount,
    ...rest,
  }
}

export const useApprovalRequestsSearchPagination = (props: SearchProps = {}) => {
  const { approvalRequests, totalCount, ...rest } = useApprovalRequestsSearch(props)

  const [pageQueryParams, setPage, setItemsPerPage] = usePaginationQueryParams()
  const { page, itemsPerPage } = pageQueryParams
  const totalPages = Math.ceil((totalCount || 0) / itemsPerPage)

  return {
    approvalRequests,
    ...rest,
    page,
    itemsPerPage,
    totalPages,
    setPage,
    setItemsPerPage,
  }
}
